import React, { useState, useEffect } from 'react';
import {
  TrendingUp, Target, PieChart, AlertTriangle, Award,
  RefreshCw, Loader, DollarSign, Percent, BarChart3,
  Activity, Zap, CheckCircle, ArrowRight, Info, Clock
} from 'lucide-react';

const AdvancedPortfolioAnalytics = ({ portfolio }) => {
  const [loading, setLoading] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  useEffect(() => {
    if (portfolio && portfolio.length > 0) {
      analyzePortfolio();
    }
  }, [portfolio]);

  const analyzePortfolio = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/portfolio-analytics/analyze`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          holdings: portfolio
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to analyze portfolio');
      }
      
      const data = await response.json();
      setAnalytics(data);
    } catch (err) {
      console.error('Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        background: 'rgba(30, 41, 59, 0.5)',
        border: '1px solid #334155',
        borderRadius: '0.75rem',
        padding: '3rem',
        textAlign: 'center'
      }}>
        <Loader size={32} className="spin" style={{ color: '#60a5fa', margin: '0 auto' }} />
        <p style={{ color: '#94a3b8', marginTop: '1rem' }}>Analyzing your portfolio...</p>
      </div>
    );
  }

  if (error || !analytics) {
    return (
      <div style={{
        background: 'rgba(239, 68, 68, 0.1)',
        border: '1px solid rgba(239, 68, 68, 0.3)',
        borderRadius: '0.75rem',
        padding: '1.5rem',
        textAlign: 'center'
      }}>
        <p style={{ color: '#f87171', marginBottom: '1rem' }}>
          {error || 'No analytics data available'}
        </p>
        <button
          onClick={analyzePortfolio}
          style={{
            padding: '0.5rem 1rem',
            background: '#ef4444',
            border: 'none',
            borderRadius: '0.5rem',
            color: 'white',
            cursor: 'pointer',
            fontWeight: '600'
          }}
        >
          Retry Analysis
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1.5rem',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <BarChart3 size={24} color="#60a5fa" />
          <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600', color: '#f1f5f9' }}>
            Advanced Portfolio Analytics
          </h3>
        </div>
        
        <button
          onClick={analyzePortfolio}
          style={{
            padding: '0.5rem 1rem',
            borderRadius: '0.375rem',
            border: '1px solid #475569',
            background: '#334155',
            color: '#cbd5e1',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#60a5fa';
            e.currentTarget.style.background = 'rgba(96, 165, 250, 0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#475569';
            e.currentTarget.style.background = '#334155';
          }}
        >
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      {/* Performance Comparison Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '1rem',
        marginBottom: '2rem'
      }}>
        {/* Current Portfolio */}
        <div style={{
          background: 'rgba(30, 41, 59, 0.5)',
          border: '1px solid #334155',
          borderRadius: '0.75rem',
          padding: '1.5rem'
        }}>
          <div style={{ fontSize: '0.875rem', color: '#94a3b8', marginBottom: '1rem', fontWeight: '600' }}>
            Current Portfolio
          </div>
          <div style={{ display: 'grid', gap: '0.75rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '0.875rem', color: '#cbd5e1' }}>Return:</span>
              <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#f1f5f9' }}>
                {analytics.current.portfolioReturn}%
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '0.875rem', color: '#cbd5e1' }}>Risk:</span>
              <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#f1f5f9' }}>
                {analytics.current.portfolioRisk}%
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '0.875rem', color: '#cbd5e1' }}>Sharpe Ratio:</span>
              <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#60a5fa' }}>
                {analytics.current.sharpeRatio}
              </span>
            </div>
          </div>
        </div>

        {/* Optimal Portfolio */}
        <div style={{
          background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1), transparent)',
          border: '2px solid rgba(16, 185, 129, 0.3)',
          borderRadius: '0.75rem',
          padding: '1.5rem'
        }}>
          <div style={{ fontSize: '0.875rem', color: '#10b981', marginBottom: '1rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Award size={16} />
            Optimized Portfolio
          </div>
          <div style={{ display: 'grid', gap: '0.75rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '0.875rem', color: '#cbd5e1' }}>Return:</span>
              <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#10b981' }}>
                {analytics.optimal.portfolioReturn}%
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '0.875rem', color: '#cbd5e1' }}>Risk:</span>
              <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#10b981' }}>
                {analytics.optimal.portfolioRisk}%
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '0.875rem', color: '#cbd5e1' }}>Sharpe Ratio:</span>
              <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#10b981' }}>
                {analytics.optimal.sharpeRatio}
              </span>
            </div>
          </div>
        </div>

        {/* Improvement Potential */}
        <div style={{
          background: 'rgba(30, 41, 59, 0.5)',
          border: '1px solid #334155',
          borderRadius: '0.75rem',
          padding: '1.5rem'
        }}>
          <div style={{ fontSize: '0.875rem', color: '#94a3b8', marginBottom: '1rem', fontWeight: '600' }}>
            Improvement Potential
          </div>
          <div style={{ display: 'grid', gap: '0.75rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '0.875rem', color: '#cbd5e1' }}>Return:</span>
              <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#60a5fa' }}>
                +{(analytics.optimal.portfolioReturn - analytics.current.portfolioReturn).toFixed(2)}%
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '0.875rem', color: '#cbd5e1' }}>Risk Reduction:</span>
              <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#10b981' }}>
                -{(analytics.current.portfolioRisk - analytics.optimal.portfolioRisk).toFixed(2)}%
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '0.875rem', color: '#cbd5e1' }}>Sharpe Improvement:</span>
              <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#60a5fa' }}>
                +{(analytics.optimal.sharpeRatio - analytics.current.sharpeRatio).toFixed(3)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: '0.5rem',
        marginBottom: '1.5rem',
        borderBottom: '2px solid #334155',
        paddingBottom: '0.5rem',
        overflowX: 'auto'
      }}>
        {['overview', 'optimization', 'rebalancing', 'tax-loss', 'monte-carlo'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '0.375rem 0.375rem 0 0',
              border: 'none',
              background: activeTab === tab ? 'rgba(96, 165, 250, 0.2)' : 'transparent',
              color: activeTab === tab ? '#60a5fa' : '#cbd5e1',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: activeTab === tab ? '600' : '400',
              textTransform: 'capitalize',
              whiteSpace: 'nowrap',
              transition: 'all 0.2s'
            }}
          >
            {tab.replace('-', ' ')}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div>
          {/* Diversification Metrics */}
          <div style={{
            background: 'rgba(30, 41, 59, 0.5)',
            border: '1px solid #334155',
            borderRadius: '0.75rem',
            padding: '1.5rem',
            marginBottom: '1.5rem'
          }}>
            <h4 style={{ margin: '0 0 1rem 0', color: '#f1f5f9', fontSize: '1rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <PieChart size={20} color="#8b5cf6" />
              Diversification Analysis
            </h4>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '1rem'
            }}>
              <MetricBox
                label="Diversification Score"
                value={Math.round(analytics.diversification.diversificationScore)}
                suffix="/100"
                color={getDiversificationColor(analytics.diversification.diversificationScore)}
              />
              <MetricBox
                label="Effective Holdings"
                value={analytics.diversification.effectiveHoldings}
                color="#60a5fa"
              />
              <MetricBox
                label="Top 5 Concentration"
                value={analytics.diversification.top5Concentration}
                suffix="%"
                color={analytics.diversification.top5Concentration > 60 ? '#fbbf24' : '#10b981'}
              />
              <MetricBox
                label="HHI Index"
                value={analytics.diversification.herfindahlIndex}
                color="#8b5cf6"
              />
            </div>
          </div>

          {/* Sector Exposure */}
          {analytics.sectorExposure && (
            <div style={{
              background: 'rgba(30, 41, 59, 0.5)',
              border: '1px solid #334155',
              borderRadius: '0.75rem',
              padding: '1.5rem',
              marginBottom: '1.5rem'
            }}>
              <h4 style={{ margin: '0 0 1rem 0', color: '#f1f5f9', fontSize: '1rem', fontWeight: '600' }}>
                Sector Exposure
              </h4>
              <div style={{ display: 'grid', gap: '0.75rem' }}>
                {analytics.sectorExposure.map((sector, idx) => (
                  <div key={idx}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                      <span style={{ fontSize: '0.875rem', color: '#cbd5e1' }}>{sector.sector}</span>
                      <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#f1f5f9' }}>
                        {sector.percentage}%
                      </span>
                    </div>
                    <div style={{
                      height: '8px',
                      background: 'rgba(15, 23, 42, 0.6)',
                      borderRadius: '4px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        height: '100%',
                        width: `${sector.percentage}%`,
                        background: `linear-gradient(to right, ${getSectorColor(idx)}, ${getSectorColor(idx)}cc)`,
                        transition: 'width 0.5s ease'
                      }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Correlation Matrix */}
          <div style={{
            background: 'rgba(30, 41, 59, 0.5)',
            border: '1px solid #334155',
            borderRadius: '0.75rem',
            padding: '1.5rem'
          }}>
            <h4 style={{ margin: '0 0 1rem 0', color: '#f1f5f9', fontSize: '1rem', fontWeight: '600' }}>
              Asset Correlation Matrix
            </h4>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '400px' }}>
                <thead>
                  <tr>
                    <th style={{ padding: '0.5rem', textAlign: 'left', fontSize: '0.75rem', color: '#94a3b8' }}>
                      Symbol
                    </th>
                    {analytics.correlationMatrix.map((row, idx) => (
                      <th key={idx} style={{ padding: '0.5rem', textAlign: 'center', fontSize: '0.75rem', color: '#94a3b8' }}>
                        {row.symbol}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {analytics.correlationMatrix.map((row, rowIdx) => (
                    <tr key={rowIdx}>
                      <td style={{ padding: '0.5rem', fontSize: '0.75rem', fontWeight: '600', color: '#f1f5f9' }}>
                        {row.symbol}
                      </td>
                      {row.correlations.map((corr, colIdx) => (
                        <td
                          key={colIdx}
                          style={{
                            padding: '0.5rem',
                            textAlign: 'center',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            background: getCorrelationColor(corr),
                            color: '#f1f5f9'
                          }}
                        >
                          {corr.toFixed(2)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Optimization Tab */}
      {activeTab === 'optimization' && (
        <div>
          <div style={{
            background: 'rgba(30, 41, 59, 0.5)',
            border: '1px solid #334155',
            borderRadius: '0.75rem',
            padding: '1.5rem'
          }}>
            <h4 style={{ margin: '0 0 1rem 0', color: '#f1f5f9', fontSize: '1rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Target size={20} color="#10b981" />
              Efficient Frontier
            </h4>
            <p style={{ color: '#94a3b8', fontSize: '0.875rem', marginBottom: '1.5rem' }}>
              Your current portfolio vs. optimal portfolio on the efficient frontier
            </p>
            
            <EfficientFrontierChart
              data={analytics.efficientFrontier}
              current={analytics.current}
              optimal={analytics.optimal}
            />
          </div>
        </div>
      )}

      {/* Rebalancing Tab */}
      {activeTab === 'rebalancing' && (
        <div>
          {analytics.rebalancing.needed ? (
            <div>
              <div style={{
                background: 'rgba(251, 191, 36, 0.1)',
                border: '1px solid rgba(251, 191, 36, 0.3)',
                borderRadius: '0.75rem',
                padding: '1.5rem',
                marginBottom: '1.5rem',
                display: 'flex',
                alignItems: 'start',
                gap: '1rem'
              }}>
                <AlertTriangle size={24} color="#fbbf24" style={{ flexShrink: 0 }} />
                <div>
                  <h4 style={{ margin: '0 0 0.5rem 0', color: '#fbbf24', fontSize: '1rem', fontWeight: '600' }}>
                    Rebalancing Recommended
                  </h4>
                  <p style={{ color: '#fcd34d', fontSize: '0.875rem', margin: 0 }}>
                    Your portfolio has drifted from optimal allocation. Consider the following trades:
                  </p>
                </div>
              </div>

              <div style={{
                background: 'rgba(30, 41, 59, 0.5)',
                border: '1px solid #334155',
                borderRadius: '0.75rem',
                padding: '1.5rem'
              }}>
                <h4 style={{ margin: '0 0 1rem 0', color: '#f1f5f9', fontSize: '1rem', fontWeight: '600' }}>
                  Recommended Trades
                </h4>
                <div style={{ display: 'grid', gap: '0.75rem' }}>
                  {analytics.rebalancing.trades.map((trade, idx) => (
                    <div
                      key={idx}
                      style={{
                        background: 'rgba(15, 23, 42, 0.6)',
                        border: `1px solid ${trade.action === 'BUY' ? '#10b981' : '#ef4444'}44`,
                        borderRadius: '0.5rem',
                        padding: '1rem',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: '1rem'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <div style={{
                          padding: '0.5rem',
                          borderRadius: '0.375rem',
                          background: trade.action === 'BUY' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)'
                        }}>
                          <span style={{
                            fontSize: '0.875rem',
                            fontWeight: '700',
                            color: trade.action === 'BUY' ? '#10b981' : '#ef4444'
                          }}>
                            {trade.action}
                          </span>
                        </div>
                        <div>
                          <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#f1f5f9' }}>
                            {trade.symbol}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                            Current: {trade.currentWeight}% → Target: {trade.targetWeight}%
                          </div>
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{
                          fontSize: '1rem',
                          fontWeight: '700',
                          color: trade.action === 'BUY' ? '#10b981' : '#ef4444'
                        }}>
                          {trade.difference > 0 ? '+' : ''}{trade.difference}%
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div style={{
              background: 'rgba(16, 185, 129, 0.1)',
              border: '1px solid rgba(16, 185, 129, 0.3)',
              borderRadius: '0.75rem',
              padding: '2rem',
              textAlign: 'center'
            }}>
              <CheckCircle size={48} color="#10b981" style={{ margin: '0 auto 1rem' }} />
              <h4 style={{ margin: '0 0 0.5rem 0', color: '#10b981', fontSize: '1.125rem', fontWeight: '600' }}>
                Portfolio Well Balanced
              </h4>
              <p style={{ color: '#94a3b8', fontSize: '0.875rem', margin: 0 }}>
                Your portfolio is close to optimal allocation. No rebalancing needed at this time.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Tax-Loss Tab */}
      {activeTab === 'tax-loss' && (
        <div>
          {analytics.taxLossHarvesting.opportunities > 0 ? (
            <div>
              <div style={{
                background: 'rgba(16, 185, 129, 0.1)',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                borderRadius: '0.75rem',
                padding: '1.5rem',
                marginBottom: '1.5rem',
                display: 'flex',
                alignItems: 'start',
                gap: '1rem'
              }}>
                <DollarSign size={24} color="#10b981" style={{ flexShrink: 0 }} />
                <div>
                  <h4 style={{ margin: '0 0 0.5rem 0', color: '#10b981', fontSize: '1rem', fontWeight: '600' }}>
                    Tax-Loss Harvesting Opportunities Found
                  </h4>
                  <p style={{ color: '#6ee7b7', fontSize: '0.875rem', margin: 0 }}>
                    Potential tax savings: ${analytics.taxLossHarvesting.totalPotentialSavings.toFixed(2)}
                  </p>
                </div>
              </div>

              <div style={{
                background: 'rgba(30, 41, 59, 0.5)',
                border: '1px solid #334155',
                borderRadius: '0.75rem',
                padding: '1.5rem'
              }}>
                <h4 style={{ margin: '0 0 1rem 0', color: '#f1f5f9', fontSize: '1rem', fontWeight: '600' }}>
                  Recommendations
                </h4>
                <div style={{ display: 'grid', gap: '1rem' }}>
                  {analytics.taxLossHarvesting.details.map((opp, idx) => (
                    <div
                      key={idx}
                      style={{
                        background: 'rgba(15, 23, 42, 0.6)',
                        border: '1px solid rgba(16, 185, 129, 0.3)',
                        borderRadius: '0.5rem',
                        padding: '1.5rem'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: '1rem' }}>
                        <div>
                          <div style={{ fontSize: '1rem', fontWeight: '600', color: '#f1f5f9', marginBottom: '0.25rem' }}>
                            {opp.symbol}
                          </div>
                          <div style={{ fontSize: '0.875rem', color: '#94a3b8' }}>
                            {opp.shares} shares @ ${opp.costBasis.toFixed(2)}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: '0.875rem', color: '#94a3b8', marginBottom: '0.25rem' }}>
                            Unrealized Loss
                          </div>
                          <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#ef4444' }}>
                            -${opp.unrealizedLoss.toFixed(2)}
                          </div>
                        </div>
                      </div>
                      <div style={{
                        background: 'rgba(16, 185, 129, 0.1)',
                        border: '1px solid rgba(16, 185, 129, 0.3)',
                        borderRadius: '0.375rem',
                        padding: '1rem'
                      }}>
                        <div style={{ fontSize: '0.875rem', color: '#10b981', marginBottom: '0.5rem', fontWeight: '600' }}>
                          💰 Potential Tax Savings: ${opp.taxSavings.toFixed(2)}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                          {opp.recommendation}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div style={{
              background: 'rgba(30, 41, 59, 0.5)',
              border: '1px solid #334155',
              borderRadius: '0.75rem',
              padding: '2rem',
              textAlign: 'center'
            }}>
              <Info size={48} color="#60a5fa" style={{ margin: '0 auto 1rem' }} />
              <h4 style={{ margin: '0 0 0.5rem 0', color: '#f1f5f9', fontSize: '1.125rem', fontWeight: '600' }}>
                No Tax-Loss Opportunities
              </h4>
              <p style={{ color: '#94a3b8', fontSize: '0.875rem', margin: 0 }}>
                No positions with significant unrealized losses found at this time.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Monte Carlo Tab */}
      {activeTab === 'monte-carlo' && (
        <div>
          <div style={{
            background: 'rgba(30, 41, 59, 0.5)',
            border: '1px solid #334155',
            borderRadius: '0.75rem',
            padding: '1.5rem',
            marginBottom: '1.5rem'
          }}>
            <h4 style={{ margin: '0 0 1rem 0', color: '#f1f5f9', fontSize: '1rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Activity size={20} color="#8b5cf6" />
              Monte Carlo Simulation (10 Years)
            </h4>
            <p style={{ color: '#94a3b8', fontSize: '0.875rem', marginBottom: '1.5rem' }}>
              1,000 simulations showing potential portfolio outcomes
            </p>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: '1rem',
              marginBottom: '1.5rem'
            }}>
              <div style={{
                background: 'rgba(15, 23, 42, 0.6)',
                border: '1px solid #334155',
                borderRadius: '0.5rem',
                padding: '1rem'
              }}>
                <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.25rem' }}>Best Case</div>
                <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#10b981' }}>
                  ${analytics.monteCarlo.results.best.toLocaleString()}
                </div>
              </div>
              <div style={{
                background: 'rgba(15, 23, 42, 0.6)',
                border: '1px solid #334155',
                borderRadius: '0.5rem',
                padding: '1rem'
              }}>
                <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.25rem' }}>90th Percentile</div>
                <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#6ee7b7' }}>
                  ${analytics.monteCarlo.results.percentile90.toLocaleString()}
                </div>
              </div>
              <div style={{
                background: 'rgba(15, 23, 42, 0.6)',
                border: '1px solid #334155',
                borderRadius: '0.5rem',
                padding: '1rem'
              }}>
                <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.25rem' }}>Median</div>
                <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#60a5fa' }}>
                  ${analytics.monteCarlo.results.median.toLocaleString()}
                </div>
              </div>
              <div style={{
                background: 'rgba(15, 23, 42, 0.6)',
                border: '1px solid #334155',
                borderRadius: '0.5rem',
                padding: '1rem'
              }}>
                <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.25rem' }}>10th Percentile</div>
                <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#fbbf24' }}>
                  ${analytics.monteCarlo.results.percentile10.toLocaleString()}
                </div>
              </div>
              <div style={{
                background: 'rgba(15, 23, 42, 0.6)',
                border: '1px solid #334155',
                borderRadius: '0.5rem',
                padding: '1rem'
              }}>
                <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.25rem' }}>Worst Case</div>
                <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#ef4444' }}>
                  ${analytics.monteCarlo.results.worst.toLocaleString()}
                </div>
              </div>
            </div>

            <div style={{
              background: 'rgba(59, 130, 246, 0.1)',
              border: '1px solid rgba(59, 130, 246, 0.3)',
              borderRadius: '0.5rem',
              padding: '1rem'
            }}>
              <div style={{ fontSize: '0.875rem', color: '#60a5fa', marginBottom: '0.5rem', fontWeight: '600' }}>
                💡 Interpretation
              </div>
              <div style={{ fontSize: '0.75rem', color: '#cbd5e1', lineHeight: '1.6' }}>
                Starting value: ${analytics.monteCarlo.initialValue.toLocaleString()}<br/>
                There's a 50% chance your portfolio will be worth ${analytics.monteCarlo.results.median.toLocaleString()} or more in 10 years.<br/>
                There's a 10% chance it could reach ${analytics.monteCarlo.results.percentile90.toLocaleString()} or higher.
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper Components
const MetricBox = ({ label, value, suffix = '', color }) => (
  <div style={{
    background: 'rgba(15, 23, 42, 0.6)',
    border: '1px solid #334155',
    borderRadius: '0.5rem',
    padding: '1rem'
  }}>
    <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.5rem' }}>
      {label}
    </div>
    <div style={{ fontSize: '1.5rem', fontWeight: '700', color }}>
      {value}{suffix}
    </div>
  </div>
);

const EfficientFrontierChart = ({ data, current, optimal }) => {
  if (!data || data.length === 0) return null;

  const width = 600;
  const height = 400;
  const padding = { top: 20, right: 20, bottom: 60, left: 60 };

  const risks = data.map(d => d.risk);
  const returns = data.map(d => d.return);
  
  const minRisk = Math.min(...risks, current.portfolioRisk, optimal.portfolioRisk);
  const maxRisk = Math.max(...risks, current.portfolioRisk, optimal.portfolioRisk);
  const minReturn = Math.min(...returns, current.portfolioReturn, optimal.portfolioReturn);
  const maxReturn = Math.max(...returns, current.portfolioReturn, optimal.portfolioReturn);

  const xScale = (risk) => 
    padding.left + ((risk - minRisk) / (maxRisk - minRisk)) * (width - padding.left - padding.right);
  const yScale = (ret) => 
    height - padding.bottom - ((ret - minReturn) / (maxReturn - minReturn)) * (height - padding.top - padding.bottom);

  const linePath = data
    .map((d, i) => `${i === 0 ? 'M' : 'L'} ${xScale(d.risk)},${yScale(d.return)}`)
    .join(' ');

  return (
    <div style={{ overflowX: 'auto' }}>
      <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} style={{ maxWidth: '100%' }}>
        {/* Grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
          const y = padding.top + ratio * (height - padding.top - padding.bottom);
          return (
            <line
              key={`h-${ratio}`}
              x1={padding.left}
              y1={y}
              x2={width - padding.right}
              y2={y}
              stroke="#334155"
              strokeWidth="1"
              strokeDasharray="4,4"
            />
          );
        })}

        {/* Efficient Frontier */}
        <path
          d={linePath}
          fill="none"
          stroke="#60a5fa"
          strokeWidth="3"
        />

        {/* Current Portfolio */}
        <circle
          cx={xScale(current.portfolioRisk)}
          cy={yScale(current.portfolioReturn)}
          r="8"
          fill="#fbbf24"
          stroke="white"
          strokeWidth="2"
        />
        <text
          x={xScale(current.portfolioRisk)}
          y={yScale(current.portfolioReturn) - 15}
          textAnchor="middle"
          fill="#fbbf24"
          fontSize="12"
          fontWeight="bold"
        >
          Current
        </text>

        {/* Optimal Portfolio */}
        <circle
          cx={xScale(optimal.portfolioRisk)}
          cy={yScale(optimal.portfolioReturn)}
          r="8"
          fill="#10b981"
          stroke="white"
          strokeWidth="2"
        />
        <text
          x={xScale(optimal.portfolioRisk)}
          y={yScale(optimal.portfolioReturn) - 15}
          textAnchor="middle"
          fill="#10b981"
          fontSize="12"
          fontWeight="bold"
        >
          Optimal
        </text>

        {/* Axes labels */}
        <text
          x={width / 2}
          y={height - 10}
          textAnchor="middle"
          fill="#94a3b8"
          fontSize="12"
        >
          Risk (%)
        </text>
        <text
          x={15}
          y={height / 2}
          textAnchor="middle"
          fill="#94a3b8"
          fontSize="12"
          transform={`rotate(-90, 15, ${height / 2})`}
        >
          Return (%)
        </text>
      </svg>
    </div>
  );
};

// Helper functions
function getDiversificationColor(score) {
  if (score >= 70) return '#10b981';
  if (score >= 50) return '#60a5fa';
  if (score >= 30) return '#fbbf24';
  return '#ef4444';
}

function getSectorColor(index) {
  const colors = ['#60a5fa', '#10b981', '#8b5cf6', '#f59e0b', '#ec4899', '#14b8a6'];
  return colors[index % colors.length];
}

function getCorrelationColor(corr) {
  const value = parseFloat(corr);
  if (value > 0.7) return 'rgba(239, 68, 68, 0.3)';
  if (value > 0.3) return 'rgba(251, 191, 36, 0.3)';
  if (value > -0.3) return 'rgba(96, 165, 250, 0.3)';
  return 'rgba(16, 185, 129, 0.3)';
}

export default AdvancedPortfolioAnalytics;